import { showToast } from '../../../../../utils/toast.util';
import * as React from 'react';
import { Input as TextInput } from '../../../../../../components/ui';
import { Select } from '../../../../../../components/ui';
import { Trash2, Plus } from 'lucide-react';
import { Button } from '../../../../../../components/ui';
import {
  PricingRulesContainer,
  RuleItem,
  RemoveButton,
} from './PricingRules.style';

declare const M: any;

interface PricingRule {
  id?: string | number;
  metricType: 'users' | 'filials' | 'revenue_payment_online';
  metricThreshold: number;
  basePrice: number;
  description: string;
  order: number;
}

interface PricingRulesProps {
  rules: PricingRule[];
  planType: string;
  onRulesChange: (rules: PricingRule[]) => void;
  errors?: any;
}

export const PricingRules: React.FC<PricingRulesProps> = ({
  rules = [],
  planType,
  onRulesChange,
  errors = {},
}) => {
  const [localRules, setLocalRules] = React.useState<PricingRule[]>(rules);

  const handleAddRule = () => {
    const newRule: PricingRule = {
      metricType: 'users',
      metricThreshold: 100,
      basePrice: 400,
      description: '',
      order: localRules.length + 1,
    };
    const updated = [...localRules, newRule];
    setLocalRules(updated);
    onRulesChange(updated);
  };

  const handleUpdateRule = (
    index: number,
    field: keyof PricingRule,
    value: any,
  ) => {
    if (field === 'metricType') {
      const metricExists = localRules.some(
        (rule, idx) =>
          idx !== index &&
          rule.metricType === (value as typeof rule.metricType),
      );
      if (metricExists) {
        showToast({
          html: `Já existe uma regra para ${value}. Máximo 1 regra por métrica.`,
          displayLength: 3000,
        });
        return;
      }
    }

    const updated = [...localRules];
    updated[index] = {...updated[index], [field]: value};
    setLocalRules(updated);
    onRulesChange(updated);
  };

  const handleRemoveRule = (index: number) => {
    const updated = localRules.filter((_, i) => i !== index);
    setLocalRules(updated);
    onRulesChange(updated);
  };

  const getAvailableMetrics = () => {
    const usedMetrics = localRules.map((rule) => rule.metricType);
    const allMetrics: Array<{
      value: 'users' | 'filials' | 'revenue_payment_online';
      label: string;
    }> = [
      {value: 'users', label: 'Usuários'},
      {value: 'filials', label: 'Filiais'},
      {value: 'revenue_payment_online', label: 'Receita Online'},
    ];
    return allMetrics.filter((metric) => !usedMetrics.includes(metric.value));
  };

  const canAddMoreRules = () => {
    const totalMetrics = 3;
    return localRules.length < totalMetrics;
  };

  // Mostrar seção de preços variáveis apenas para tipos dinâmicos
  const isVariablePricing = [
    'users',
    'filials',
    'revenue_payment_online',
  ].includes(planType);

  if (!isVariablePricing) {
    return null;
  }

  return (
    <PricingRulesContainer>
      <div className="row">
        <div className="col s12">
          <h5>💰 Regras de Preço Variável</h5>
          <p className="grey-text">
            Configure como o preço varia baseado em métricas da conta. As regras
            se somam para calcular o preço total.
          </p>
        </div>
      </div>

      {localRules.map((rule, index) => (
        <RuleItem key={index} className="card">
          <div className="card-content">
            <div className="row">
              <div className="col s12 m4">
                <Select
                  id={`metricType-${index}`}
                  name={`metricType-${index}`}
                  value={rule.metricType}
                  onChange={(e) =>
                    handleUpdateRule(index, 'metricType', e.target.value)
                  }
                  label="Métrica"
                  s={12}>
                  <option value="">Selecione</option>
                  <option value="users">Usuários</option>
                  <option value="filials">Filiais</option>
                  <option value="revenue_payment_online">Receita Online</option>
                </Select>
              </div>

              <TextInput
                s={12}
                m={2}
                id={`threshold-${index}`}
                type="number"
                label="A cada X"
                value={String(rule.metricThreshold || '')}
                onChange={(e) =>
                  handleUpdateRule(
                    index,
                    'metricThreshold',
                    parseInt(e.target.value),
                  )
                }
              />

              <TextInput
                s={12}
                m={2}
                id={`price-${index}`}
                type="number"
                label="Cobrar"
                value={String(rule.basePrice || '')}
                onChange={(e) =>
                  handleUpdateRule(
                    index,
                    'basePrice',
                    parseFloat(e.target.value),
                  )
                }
              />

              <TextInput
                s={12}
                m={4}
                id={`description-${index}`}
                label="Descrição"
                placeholder="Ex: 100 usuários por $400"
                value={rule.description || ''}
                onChange={(e) =>
                  handleUpdateRule(index, 'description', e.target.value)
                }
              />

              <RemoveButton
                className="btn btn-small btn-danger"
                onClick={() => handleRemoveRule(index)}>
                <Trash2 size={16} /> Remover
              </RemoveButton>
            </div>

            <div className="row">
              <div className="col s12">
                <small className="grey-text">
                  <strong>Cálculo:</strong> A cada {rule.metricThreshold}{' '}
                  {rule.metricType}s, cobrar ${rule.basePrice}
                </small>
              </div>
            </div>
          </div>
        </RuleItem>
      ))}

      <div className="row">
        <div className="col s12">
          <Button
            onClick={handleAddRule}
            disabled={!canAddMoreRules()}>
            <Plus size={16} className="mr-1" />
            Adicionar Regra de Preço
          </Button>
          {!canAddMoreRules() && (
            <small
              style={{
                marginLeft: '10px',
                color: '#999',
                display: 'inline-block',
              }}>
              (Máximo 3 regras: 1 por métrica)
            </small>
          )}
        </div>
      </div>

      {localRules.length > 0 && (
        <div className="row card-panel blue-text">
          <div className="col s12">
            <strong>Exemplo de Cálculo:</strong>
            <ul style={{marginLeft: '20px'}}>
              {localRules.map((rule, idx) => (
                <li key={idx}>
                  {rule.description ||
                    `${rule.metricType}: ${rule.metricThreshold} → $${rule.basePrice}`}
                </li>
              ))}
              <li>
                <strong>
                  Todas as regras se somam para calcular o preço total
                </strong>
              </li>
            </ul>
          </div>
        </div>
      )}
    </PricingRulesContainer>
  );
};

export default PricingRules;
